import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  ChevronRight,
  MessageCircle,
  MessageSquare,
  Check,
  Loader2,
  Paperclip,
} from 'lucide-react';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { Chip } from '@/components/ui/Chip';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useUpdateFulfillment } from '@/hooks/useTransactions';
import { usePaymentProofCounts } from '@/hooks/usePaymentProofs';
import { formatIDR, formatNumber, formatDateTime } from '@/lib/format';
import type { AppTransaction, PaymentStatus } from '@/types/domain';

const STATUS_VARIANT: Record<
  PaymentStatus,
  'success' | 'warning' | 'info' | 'danger' | 'default'
> = {
  paid: 'success',
  pending_delivery: 'warning',
  pending_payment: 'info',
  overdue: 'danger',
  partially_returned: 'info',
  returned: 'danger',
  cancelled: 'default',
};

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: 'Paid',
  pending_delivery: 'Pending',
  pending_payment: 'Tempo',
  overdue: 'Overdue',
  partially_returned: 'Partial return',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

export interface TransactionListProps {
  transactions: AppTransaction[];
  isLoading?: boolean;
  /** When true, row click is disabled (e.g. embedded in a parent). */
  noNavigate?: boolean;
  /**
   * When the list spans multiple events, show the event name as a
   * subtitle next to the invoice number. Off by default because most
   * callers already scope the list to a single event (e.g. Dashboard
   * with an event filter, EventTransactionsList) — showing it would
   * just be noise. Turn it on for the global /transactions page.
   */
  showEventName?: boolean;
  /** Lookup map id → event name. Required when `showEventName` is true. */
  eventNameById?: Map<string, string>;
}

export function TransactionList({
  transactions,
  isLoading,
  noNavigate = false,
  showEventName = false,
  eventNameById,
}: TransactionListProps) {
  const navigate = useNavigate();
  const updateFulfillment = useUpdateFulfillment();
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleMarkPaid = async (tx: AppTransaction, e: React.MouseEvent) => {
    e.stopPropagation();
    setRowError(null);
    setMarkingPaidId(tx.id);
    try {
      await updateFulfillment.mutateAsync({
        id: tx.id,
        input: { status: 'paid', notes: 'Quick mark paid from list' },
      });
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Gagal update status');
    } finally {
      setMarkingPaidId(null);
    }
  };

  // One query for the whole list: a paperclip on rows that have a proof.
  // Hooks stay above the early returns — call order must not depend on state.
  const proofIds = useMemo(() => (transactions ?? []).map((t) => t.id), [transactions]);
  const { data: proofCounts } = usePaymentProofCounts(proofIds);

  if (isLoading) {
    return (
      <div className="py-16 text-center text-ink-subtle text-body">
        Loading transactions…
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <EmptyState
        icon={<Receipt size={24} strokeWidth={1.5} />}
        title="Belum ada transaksi"
        description="Transaksi yang dibuat dari POS akan muncul di sini."
      />
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Tanggal</TH>
          <TH>Invoice</TH>
          <TH>Customer</TH>
          <TH className="text-right">Item</TH>
          <TH className="text-right">Total</TH>
          <TH>Status</TH>
          <TH>WA</TH>
          <TH className="text-right">Aksi</TH>
        </TR>
      </THead>
      <TBody>
        {transactions.map((t) => {
          const itemCount = t.items.reduce((s, i) => s + i.qty, 0);
          const isSent = t.openclawStatus === 'sent';
          const isFailed = t.openclawStatus === 'failed';
          const isSkipped = t.openclawStatus === 'skipped';
          return (
            <TR
              key={t.id}
              className={`${noNavigate ? '' : 'cursor-pointer'} ${t.paymentStatus === 'cancelled' ? 'opacity-60' : ''}`}
              onClick={
                noNavigate
                  ? undefined
                  : () => navigate(`/invoice/${t.id}`)
              }
            >
              <TD className="whitespace-nowrap text-small">
                {formatDateTime(t.createdAt)}
              </TD>
              <TD>
                <code className="text-caption font-mono text-ink inline-flex items-center gap-1">
                  {t.invoiceNumber}
                  {(proofCounts?.get(t.id) ?? 0) > 0 && (
                    <Paperclip size={11} className="text-ink-subtle" aria-label="Ada bukti pembayaran" />
                  )}
                </code>
                {showEventName && eventNameById && (
                  <p className="text-[11px] text-ink-subtle mt-0.5 truncate max-w-[180px]">
                    {eventNameById.get(t.eventId) ?? '—'}
                  </p>
                )}
              </TD>
              <TD>
                {t.customerSnapshot ? (
                  <div className="min-w-0">
                    <p className="text-body text-ink truncate max-w-[200px]">
                      {t.customerSnapshot.name}
                    </p>
                    <p className="text-caption text-ink-subtle">
                      {t.customerSnapshot.phone}
                    </p>
                  </div>
                ) : (
                  <span className="text-caption text-ink-subtle italic">
                    Walk-in
                  </span>
                )}
              </TD>
              <TD className="text-right tabular-nums text-small">
                {formatNumber(itemCount)}
              </TD>
              <TD className="text-right tabular-nums font-medium">
                {formatIDR(t.grandTotal)}
              </TD>
              <TD>
                <div className="flex flex-col gap-1 items-start">
                  <Chip variant={t.paymentStatus === 'cancelled' ? 'danger' : STATUS_VARIANT[t.paymentStatus]}>
                    {t.paymentStatus === 'cancelled' ? 'VOID' : STATUS_LABEL[t.paymentStatus]}
                  </Chip>
                  {t.fulfillment === 'later' && (
                    <Chip variant="warning">Kirim nanti</Chip>
                  )}
                </div>
              </TD>
              <TD>
                {isSent ? (
                  <Chip variant="success">
                    <MessageCircle size={11} className="mr-0.5" /> Sent
                  </Chip>
                ) : isFailed ? (
                  <Chip variant="danger">
                    <MessageCircle size={11} className="mr-0.5" /> Failed
                  </Chip>
                ) : isSkipped ? (
                  <Chip variant="default">
                    <MessageSquare size={11} className="mr-0.5" /> Skipped
                  </Chip>
                ) : (
                  <span className="text-caption text-ink-subtle">—</span>
                )}
              </TD>
              <TD
                className="text-right"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="inline-flex items-center gap-1">
                  {t.paymentStatus === 'pending_delivery' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => handleMarkPaid(t, e)}
                      loading={markingPaidId === t.id}
                      title="Tandai sudah dibayar"
                      leftIcon={
                        markingPaidId === t.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )
                      }
                    >
                      Paid
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/invoice/${t.id}`)}
                    rightIcon={<ChevronRight size={14} />}
                  >
                    Detail
                  </Button>
                </div>
                {rowError && markingPaidId === null && (
                  <p className="text-caption text-danger mt-1">{rowError}</p>
                )}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}
